// apps/web/src/main.tsx

import { StrictMode } from 'react';

import { createRoot } from 'react-dom/client';
import { Toaster } from 'react-hot-toast';

// Initialize i18n before rendering
import '@alamo/i18n';

// Phase Geofences W5 — OpenLayers + @alamo/maps stylesheets carregados
// globalmente. Sem `ol/ol.css` os controles `.ol-zoom`/`.ol-attribution` ficam
// sem estilo (visual "horrível" reportado em UAT 2026-04-28). Sem
// `@alamo/maps/styles` o `.alamo-map-container` perde altura mínima e o canvas
// pode renderizar com 0px em layouts não-flex.
import 'ol/ol.css';
import '@alamo/maps/styles';

import { App } from './App';
import './lib/chunkReload';
// Side-effect: registra o listener de `beforeinstallprompt` cedo (o browser pode
// dispará-lo antes de qualquer componente montar) para o nudge de instalação
// (InstallAppPrompt no login + item "Instalar app" do menu do avatar).
import './lib/installPrompt';
import { disablePwaForPublicRoute, isPublicNoPwaRoute } from './lib/pwaScope';
import { registerZodErrorMap } from './lib/zodErrorMap';
import './styles/global.css';

// Opção B2 — fluxos públicos de uso único do cliente rodam SEM PWA: desregistra
// qualquer service worker/precache de visita anterior e remove o manifest (não
// instalável + sempre fresco). Em rotas com PWA (login + app) nada acontece aqui;
// o registro do SW segue por conta do PwaUpdatePrompt (montado só nessas rotas).
if (isPublicNoPwaRoute(window.location.pathname)) {
  disablePwaForPublicRoute();
}

// Favicon por ambiente: PROD = laranja (default declarado no index.html); todos
// os demais (homolog, dev) = navy, pra distinguir a aba de produção da de teste
// num relance. Roda no boot, antes do render, trocando o href de cada <link
// rel="icon"> (svg + png fallbacks) para a variante `-hml`.
if (import.meta.env.VITE_ENV !== 'prod') {
  document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']").forEach((link) => {
    const href = link.getAttribute('href');
    if (href) {
      link.setAttribute('href', href.replace(/\/favicon(-\d+)?\.(svg|png)/, '/favicon-hml$1.$2'));
    }
  });
}

// Register pt-BR Zod errorMap globally (Phase 02.2.1 Plan 02 D-06, closes UAT22-05
// class). Must run before any form mounts so invalid_type/too_small/invalid_format
// issues fall back to common:validation.* keys instead of Zod's English defaults.
registerZodErrorMap();

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

/**
 * Toast duration convention (Plan 02.1.1.1.1-06, D-06 polish):
 * - success: 4000ms (default) — quick confirmation, user already acted
 * - error:   5000ms           — needs a beat to read the failure reason
 * - auth errors (401): 6000ms — caller MUST opt-in via `toast.error(msg, { duration: 6000 })`;
 *                                the extra second accommodates "sua sessao expirou"
 *                                which requires reading + a navigation decision.
 * Keep this contract when adding new toast call sites — do not override `duration`
 * below 4000ms, and reserve 6000ms for session-expired / critical auth failures.
 */
createRoot(root).render(
  <StrictMode>
    <App />
    <Toaster
      position="top-right"
      containerStyle={{ top: 64, right: 16 }}
      gutter={8}
      toastOptions={{
        duration: 4000,
        className: 'alamo-toast',
        style: {
          fontFamily: 'Poppins, sans-serif',
          fontSize: '0.875rem',
          maxWidth: '380px',
        },
        success: {
          duration: 4000,
          style: { borderLeft: '4px solid var(--color-success-500)' },
        },
        error: {
          duration: 5000,
          style: { borderLeft: '4px solid var(--color-error-500)' },
        },
      }}
    />
  </StrictMode>,
);
